import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Alert, AlertTitle } from '@mui/material';
import { Refresh, BugReport } from '@mui/icons-material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console and potentially to error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log to error reporting service if available
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            p: 3,
            textAlign: 'center'
          }}
        >
          <Alert 
            severity="error" 
            sx={{ mb: 3, width: '100%', maxWidth: '500px' }}
            icon={<BugReport />}
          >
            <AlertTitle>Something went wrong</AlertTitle>
            {this.props.showErrorDetails && this.state.error && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {this.state.error.toString()}
              </Typography>
            )}
            {!this.props.showErrorDetails && (
              <Typography variant="body2">
                We encountered an unexpected error. Please try refreshing the component.
              </Typography>
            )}
          </Alert>
          
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={this.handleRetry}
            sx={{ mb: 2 }}
          >
            Try Again
          </Button>

          {this.props.showReloadButton && (
            <Button
              variant="outlined"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  onError: PropTypes.func,
  showErrorDetails: PropTypes.bool,
  showReloadButton: PropTypes.bool
};

ErrorBoundary.defaultProps = {
  showErrorDetails: process.env.NODE_ENV === 'development',
  showReloadButton: true
};

export default ErrorBoundary;
